import usePlan from '../composables/usePlan'
import useBetterChoiceService from './useBetterChoiceService'
import useSort from '../composables/useSort'
import useIsLocalArea from './useTravelNetworkService'
import useBenefits from './useBenefitService'
import useContext from '../composables/useContext'
import useLingo2Service from './useLingo2Service'
import useOnlineClinicService from './useOnlineClinicService'
import usePractitionerSearchService from './usePractitionerSearchService'
import useLocationSearchService from './useLocationSearchService'
import useLogger from '../composables/useLoggerLite'
import { v4 as uuid } from 'uuid'

/**
 *
 * The useSearch function is a higher order function that orchestrates the search and provides higher order functionality.  It delegates
 * each individual search to the appropriate search service for the actual search ( usePractitionerSearch, useLocationSearch ).  It has the
 * following purposes:
 * 1) Retrieve pre-requisite data - data from things like Lingo2 careConcepts that provide context to the searches.
 * 2) Execute searches alongside other data retrieval functions needed by the search on the client.
 * 3) Provide higher order ( cross cutting ) functions to be executed during the individual searches.
 * 4) Package the resulting data up for delivery to the client.
 * 
 * The following parameters are required by the function.
 * profile - The profile built up from the auth.js middleware.
 * context - The context from the model. IMPORTANT - when pulling the context out of the model, the attributes in the
 * model will be ref() objects ( Vue composition API ).  As such, each attributes value is retrieved via the 'value' attribute.
 * However, when a request is made from the browser to the express server, the context is passed via straight JSON, so there are no
 * longer any ref() references in the data.  It will be a pure JSON object.  Therefore, this service will not use .value references when
 * trying to access attributes on the context.  If calling this function from a Nuxt context where the context is not a JSON object and
 * has ref() objects referencing the values - make sure to convert the contxt into a JSON object before passing to this function.  You can
 * do this in the following manner:
 * 
 * const contextJson = JSON.parse(JSON.stringify(context.value))
 *
 */
export default function useSearchService(profile, context) {

  const { getNetworkId, getPlan } = usePlan(profile, context)
  const { hasPropertyAndData, secondarySort, getLocationSortOptions, getPractitionerSortOptions } = useSort(profile, context)
  const { searchType } = useContext(context)
  const { getOnlineClinics } = useOnlineClinicService()
  const { getBenefitInfoForSearch } = useBenefits(profile, context)
  const { getRecommendedBadgeType } = useBetterChoiceService()
  const { searchPractitioners } = usePractitionerSearchService()
  const { searchLocations } = useLocationSearchService()
  const { withTimings } = useLogger()

    // CHECK TRAVEL NETWORK AREA
  // need to check if the zip code a user is using is in the local 5-state region
  // to determine if a user with a travel network plan should be searching within their travel
  // or regular network. As this service call is very small, we are doing it during every search so
  // the information is readily available to the client
  // this information is stored as a boolean in the 'isLocalAreaTravelNetwork' variable
  const { getIsLocalArea } = useIsLocalArea(context)

  /**
   *  Execute a search.  Orchestrates a search for either a practitioner or a location.
   */
  const search = async (recordCount = 300) => {
    // currently, we are having difficulty with "reqeuestId" being passed to the asyncLocalStorage.
    // so use a UUID to associate tasks within a search.  We will need to revisit this later.
    const searchId = uuid()

    let data
    let results
    let onlineClinics
    let costEstimatesFailed = false
    let isLocalAreaTravelNetwork
    let plan = getPlan()
    let benefitCategories
    // currentPlanXXX variables are attached to the search results and used to dictate the plan details in which
    // the current displayed search results represent. otherwise, when using the plan variables from the model
    // directly, if a new plan was selected from the filter panel, the In {{ plan.name }} Network was updated
    // on the cards with the new selection before a new search took place, misleading users to assume a provider
    // is in a certain network. Reference PD-2156
    let currentPlanName
    let currentPlanTierLabel
    let currentPlanIsTiered

    const { search: lingo2Search } = useLingo2Service()

    try {
      currentPlanName = plan.name
      currentPlanTierLabel = plan.tierLabel
      currentPlanIsTiered = plan.isTiered
      const practiceType = context.type == 'dental' || context.type == 'dentist' ? 'dental' : 'medical'
      const options = { serviceType: fns.searchType().toLowerCase(), practiceType: practiceType, healthLineCode: context.code, convertForCpn: true, fromFilters: context.cc}
      
      // const { careConcepts, betterChoiceContext, rawConcepts } = await lingo2Search(context.term, getNetworkId(), options)
      const { careConcepts, betterChoiceContext, rawConcepts } = await withTimings(`lingo2Search (${searchId})`, lingo2Search, context.term, getNetworkId(), options)

      if (fns.searchType() === 'Practitioner') {
        data = await Promise.all([
          // do timings inside of searchPractitioners
          searchPractitioners({searchId, profile, context, careConcepts, betterChoiceContext, recordCount: recordCount, decorators: [ appendSearchType, appendKey] }),
          withTimings(`getRecommendedBadgeType (${searchId})`, getRecommendedBadgeType, profile, context, betterChoiceContext),
          withTimings(`getIsLocalArea (${searchId})`, getIsLocalArea),
          withTimings(`getBenefitInfoForSearch (${searchId})`, getBenefitInfoForSearch)
        ])
      } else {
        data = await Promise.all([
          // do timings inside of searchLocations
          searchLocations({searchId, profile, context, careConcepts, betterChoiceContext, recordCount: recordCount, decorators: [ appendSearchType, appendKey] }),
          withTimings(`getRecommendedBadgeType (${searchId})`, getRecommendedBadgeType, profile, context, betterChoiceContext),
          withTimings(`getOnlineClinics (${searchId})`, getOnlineClinics, profile, context, careConcepts),
          withTimings(`getIsLocalArea (${searchId})`, getIsLocalArea),
          withTimings(`getBenefitInfoForSearch (${searchId})`, getBenefitInfoForSearch)
        ])
      }
      onlineClinics = data[2]
      results = data[0]
      //need to check searchType to determine if isLocalArea data will be in 3rd or 4th place...
      if (fns.searchType() === 'Practitioner') {
        isLocalAreaTravelNetwork = data[2]
        benefitCategories = data[3]
      } else {
        isLocalAreaTravelNetwork = data[3]
        benefitCategories = data[4]
      }

      //Run a secondary sort to account for things like cost estimates.
      //Default the sortString to the sort attribute on the context.  If it does not exist ( not explicitly chosen by the user in the UI),
      //then use the first sort string in the sort options produced by the use-options composition function.
      let sortString = context.sort
      if (!sortString) {
        const options = fns.searchType() == 'Practitioner' ? getPractitionerSortOptions(results) : getLocationSortOptions(results)
        sortString = options[0].value
      }

      if (sortString) results = secondarySort(results, sortString)

      return {
        status: 'OK',
        costEstimatesFailed: costEstimatesFailed,
        //Needed for TopValue alert
        recommendedBadgeType: data[1],
        //Needed for TopValue alert
        betterChoiceContext: betterChoiceContext,
        value: results,
        onlineClinics: onlineClinics,
        isLocalAreaTravelNetwork: isLocalAreaTravelNetwork,
        currentPlanName: currentPlanName,
        currentPlanTierLabel: currentPlanTierLabel,
        currentPlanIsTiered: currentPlanIsTiered,
        benefitCategories: benefitCategories,
        rawConcepts: rawConcepts ? rawConcepts : []
      }
    } catch (error) {
      console.log("Error occured during search", error.message)
      return {
        status: 'Error',
        recommendedBadgeType: null,
        betterChoiceContext: null,
        value: error,
      }
    }
  }

  /**
   * Add the context type ( as searcgType ) to the result so that the data on the result can be used to determine the component to render it with.
   * If we try to use the context, we will get a bug when the user changes the "I am looking for a" field in the UI as the SearchReults.vue component
   * will try to render but with the wrong component for the searchResults in the model.
   */
  const appendSearchType = (result) => {
    result.searchType = context.type
  }

  /*
   * Create a unique key for each search result that is unique to the result and the request.  Do
   * this so that the v-if statement does not try to re-use items in the v-if block and cause issues
   * with the Maps not going away.
   **/
  const appendKey = (result) => {
    //careNetworkId is potentially null so check and set careNetworkId value to 'X' if null
    //not appending it to the key  will show incorrect network status in the banner for results when changing care networks
    const careNetworkId = context.careNetworkId ? context.careNetworkId : 'X'
    //tier is potentially null so check and set tier value to 'X' if null
    const tier = result.tier ? result.tier : 'X'
    //if practitioner special available use it , if not then return empty string
    const practicingSpecialty = result.practicingSpecialty ? result.practicingSpecialty : 'Z'
    //For practitioners need to create a unique key from practitionerId, locationId, tier value, and the practicing specialty at the location
    if (fns.searchType() === 'Practitioner') result.key = `${result.practitionerId}-${result.locationId}-${tier}-${practicingSpecialty}-${careNetworkId}`
    //For locations - just the id ( locationId ) and the tier value suffice.
    else result.key = `${result.id}-${tier}-${careNetworkId}`
  }

  const fns = {
    searchType,
    search,
    appendKey,
    appendSearchType,
    getIsLocalArea,
  }

  return fns
}